// @system — Honeypot: the ONE spam-protection field every public form carries (launch contract rule 18).
//
// A real browser never fills it: the input is visually hidden (`sr-only`), out of the tab order,
// excluded from autofill and hidden from assistive technology. Bots that fill every field do, and
// the server answers a filled `website` exactly like a stored submission while storing nothing
// (api/@system/contact, /auth/register, /auth/forgot-password, /auth/reset-password,
// /users/password/*). The `data-honeypot` marker is what the launch live check and the static
// contract check look for — keep it on the input. It still carries an accessible name
// (HONEYPOT_LABEL): an unnamed text input fails the label checks (e2e 04-accessibility, axe `label`)
// on every page that renders a form, even though nobody is meant to see it.
//
// Usage
//   uncontrolled (native forms):        <Honeypot />            → read with `formHoneypotFilled(form)`
//   react-hook-form:                    <Honeypot {...register('website')} />
//   controlled (useState field maps):   <Honeypot value={fields.website} onChange={handleChange} />
import { forwardRef } from 'react'

/** The field name the server-side schemas accept (`website`, optional, must be empty). */
export const HONEYPOT_FIELD = 'website'

/** Accessible name of the honeypot input (it is aria-hidden; the name satisfies label checks). */
const HONEYPOT_LABEL = 'Leave this field empty'

/** @type {ForwardRefComponent<'input', WithClassName>} */
export const Honeypot = forwardRef(function Honeypot({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      type="text"
      name={HONEYPOT_FIELD}
      data-honeypot=""
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      aria-label={HONEYPOT_LABEL}
      className={className ? `sr-only ${className}` : 'sr-only'}
      {...props}
    />
  )
})

/**
 * True when the honeypot input of a native form holds a value — a bot filled it. Callers drop the
 * submission silently (no request, no error message), which is what the server would do anyway.
 * @param {HTMLFormElement | null | undefined} form
 * @returns {boolean}
 */
export function formHoneypotFilled(form) {
  if (!form) return false
  const el = form.elements.namedItem(HONEYPOT_FIELD)
  return el instanceof HTMLInputElement && el.value.trim().length > 0
}
